import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CurrentsAPI,
  mergeModelTestStep,
  type ModelTestLogStep,
  type OceanOrgSkill,
  type OceanSkillScriptTestResult,
  type OceanModelSkillTestResult,
} from "@vida/currents-core";
import { ModelTestTracePanel } from "../components/skill-editor/ModelTestTracePanel";
import { resolveJobOutputImageUrl } from "../services/jobOutput";
import { createCurrentsApiConfig } from "../services/currentsSession";
import {
  MODEL_SAMPLE_PARAMS_PRESETS,
  SCRIPT_SAMPLE_DATA_PRESETS,
  SCRIPT_SAMPLE_INPUT_PRESETS,
  DEFAULT_MODEL_BINDING_JSON,
} from "../services/agentCatalog";

export function SkillEditorPage() {
  const { skillId } = useParams<{ skillId: string }>();
  const [skill, setSkill] = useState<OceanOrgSkill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [genDescription, setGenDescription] = useState("");
  const [genFeedback, setGenFeedback] = useState("");
  const [sampleInput, setSampleInput] = useState(SCRIPT_SAMPLE_INPUT_PRESETS.default);
  const [sampleData, setSampleData] = useState(SCRIPT_SAMPLE_DATA_PRESETS.default);
  const [testResult, setTestResult] = useState<OceanSkillScriptTestResult | null>(null);
  const [modelTestResult, setModelTestResult] = useState<OceanModelSkillTestResult | null>(null);
  const [modelTestSteps, setModelTestSteps] = useState<ModelTestLogStep[]>([]);
  const [modelTestImageUrl, setModelTestImageUrl] = useState<string | null>(null);
  const [sampleParams, setSampleParams] = useState(MODEL_SAMPLE_PARAMS_PRESETS.default);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!skillId) return;
    const cfg = createCurrentsApiConfig();
    const s = await CurrentsAPI.getOrgSkill(cfg, skillId);
    setSkill(s);
    if (s?.description) setGenDescription(s.description);
  }, [skillId]);

  useEffect(() => {
    void (async () => {
      try {
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [reload]);

  if (!skillId) {
    return <p className="err">Skill ID mancante</p>;
  }

  const isScript = (skill?.skillType ?? "TOOL_BUNDLE") === "SCRIPT";
  const isModel = (skill?.skillType ?? "TOOL_BUNDLE") === "MODEL";

  async function runAction(label: string, fn: () => Promise<void>) {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="muted" style={{ margin: 0 }}>
            <Link to="/skills">← Skills</Link>
          </p>
          <h1>{skill?.name ?? skillId}</h1>
          <p className="muted">
            Editor skill {isScript ? "SCRIPT" : skill?.skillType ?? "—"} — versione pubblicata:{" "}
            {skill?.publishedScriptVersion ?? 0}
          </p>
        </div>
      </header>

      {loading && <p className="muted">Caricamento…</p>}
      {error && <p className="err">{error}</p>}

      {skill && !isScript && !isModel && (
        <div className="card">
          <p>
            Questa skill è di tipo <code>{skill.skillType}</code>. Puoi convertirla in SCRIPT (sandbox Python) o
            MODEL (binding provider media).
          </p>
          <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn"
              disabled={!!busy}
              onClick={() =>
                void runAction("convert-script", async () => {
                  const cfg = createCurrentsApiConfig();
                  const updated = await CurrentsAPI.updateOrgSkill(cfg, skillId, {
                    skillType: "SCRIPT",
                  });
                  setSkill(updated);
                })
              }
            >
              Converti in SCRIPT
            </button>
            <button
              type="button"
              className="btn"
              disabled={!!busy}
              onClick={() =>
                void runAction("convert-model", async () => {
                  const cfg = createCurrentsApiConfig();
                  const updated = await CurrentsAPI.updateOrgSkill(cfg, skillId, {
                    skillType: "MODEL",
                    modelBindingJson: skill.modelBindingJson ?? DEFAULT_MODEL_BINDING_JSON,
                  });
                  setSkill(updated);
                })
              }
            >
              Converti in MODEL
            </button>
          </div>
        </div>
      )}

      {skill && isScript && (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Genera script</h2>
            <label className="field">
              <span>Descrizione</span>
              <textarea
                rows={4}
                value={genDescription}
                onChange={(e) => setGenDescription(e.target.value)}
                placeholder="Es. digest settimanale articoli Reef filtrati per topic"
              />
            </label>
            <label className="field">
              <span>Feedback (rigenerazione mirata)</span>
              <textarea
                rows={3}
                value={genFeedback}
                onChange={(e) => setGenFeedback(e.target.value)}
                placeholder="Es. filtra per data, formatta output come elenco puntato"
              />
            </label>
            <button
              type="button"
              className="btn primary"
              disabled={!!busy || !genDescription.trim()}
              onClick={() =>
                void runAction("generate", async () => {
                  const cfg = createCurrentsApiConfig();
                  const updated = await CurrentsAPI.generateSkillScript(
                    cfg,
                    skillId,
                    genDescription.trim(),
                    undefined,
                    {
                      currentScriptBody: skill?.draftScriptBody ?? undefined,
                      feedback: genFeedback.trim() || undefined,
                    },
                  );
                  setSkill(updated);
                  setTestResult(null);
                })
              }
            >
              {busy === "generate" ? "Generazione…" : skill?.draftScriptBody ? "Rigenera script" : "Genera script (LLM)"}
            </button>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Draft script</h2>
            <label className="field">
              <span>Python</span>
              <textarea
                rows={14}
                className="mono"
                value={skill.draftScriptBody ?? ""}
                onChange={(e) => setSkill({ ...skill, draftScriptBody: e.target.value })}
              />
            </label>
            <label className="field">
              <span>JSON Schema input</span>
              <textarea
                rows={6}
                className="mono"
                value={skill.inputSchemaJson ?? "{}"}
                onChange={(e) => setSkill({ ...skill, inputSchemaJson: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="btn"
              disabled={!!busy}
              onClick={() =>
                void runAction("save", async () => {
                  const cfg = createCurrentsApiConfig();
                  const updated = await CurrentsAPI.updateOrgSkill(cfg, skillId, {
                    draftScriptBody: skill.draftScriptBody ?? "",
                    inputSchemaJson: skill.inputSchemaJson ?? "{}",
                  });
                  setSkill(updated);
                })
              }
            >
              Salva draft
            </button>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Test sandbox</h2>
            <p className="muted">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setSampleInput(SCRIPT_SAMPLE_INPUT_PRESETS.reefDigest);
                  setSampleData(SCRIPT_SAMPLE_DATA_PRESETS.reefDigest);
                }}
              >
                Preset Reef digest
              </button>
            </p>
            <label className="field">
              <span>_INPUT campione (JSON)</span>
              <textarea
                rows={5}
                className="mono"
                value={sampleInput}
                onChange={(e) => setSampleInput(e.target.value)}
              />
            </label>
            <label className="field">
              <span>_DATA prefetched (JSON)</span>
              <textarea
                rows={5}
                className="mono"
                value={sampleData}
                onChange={(e) => setSampleData(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn"
              disabled={!!busy || !skill.draftScriptBody}
              onClick={() =>
                void runAction("test", async () => {
                  const cfg = createCurrentsApiConfig();
                  const result = await CurrentsAPI.testSkillScript(
                    cfg,
                    skillId,
                    sampleInput,
                    undefined,
                    skill.draftScriptBody ?? undefined,
                    sampleData,
                  );
                  setTestResult(result);
                })
              }
            >
              {busy === "test" ? "Esecuzione…" : "Test in sandbox"}
            </button>
            {testResult && (
              <div className="mission-io-section" style={{ marginTop: "1rem" }}>
                <div className="mission-io-title">
                  Esito (exit {testResult.exitCode}, {testResult.durationMs} ms)
                </div>
                {testResult.stdout && <pre className="mission-io-value">{testResult.stdout}</pre>}
                {testResult.stderr && <pre className="mission-io-value err">{testResult.stderr}</pre>}
                {testResult.outputJson && (
                  <pre className="mission-io-value">{testResult.outputJson}</pre>
                )}
              </div>
            )}
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Publish</h2>
            <p className="muted">
              Il binding agente usa sempre la versione pubblicata (v{skill.publishedScriptVersion}). Modifiche al
              draft non cambiano il runtime finché non pubblichi.
            </p>
            <button
              type="button"
              className="btn primary"
              disabled={!!busy || !skill.draftScriptBody}
              onClick={() =>
                void runAction("publish", async () => {
                  const cfg = createCurrentsApiConfig();
                  await CurrentsAPI.updateOrgSkill(cfg, skillId, {
                    draftScriptBody: skill.draftScriptBody ?? "",
                    inputSchemaJson: skill.inputSchemaJson ?? "{}",
                  });
                  const updated = await CurrentsAPI.publishSkillScript(cfg, skillId);
                  setSkill(updated);
                  await reload();
                })
              }
            >
              {busy === "publish" ? "Pubblicazione…" : "Publish versione immutabile"}
            </button>
          </div>
        </>
      )}

      {skill && isModel && (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>Model binding</h2>
            <p className="muted">
              JSON binding: jobKind, provider, modelId, defaults, exposeParams, promptTemplate, composerSkillId
              (opzionale).
            </p>
            <label className="field">
              <span>modelBindingJson</span>
              <textarea
                rows={16}
                className="mono"
                value={skill.modelBindingJson ?? "{}"}
                onChange={(e) => setSkill({ ...skill, modelBindingJson: e.target.value })}
              />
            </label>
            <button
              type="button"
              className="btn"
              disabled={!!busy}
              onClick={() =>
                void runAction("save-model", async () => {
                  const cfg = createCurrentsApiConfig();
                  const updated = await CurrentsAPI.updateOrgSkill(cfg, skillId, {
                    modelBindingJson: skill.modelBindingJson ?? "{}",
                  });
                  setSkill(updated);
                })
              }
            >
              Salva binding
            </button>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>Test skill MODEL</h2>
            <p className="muted">
              La generazione immagine può richiedere fino a 2–3 minuti: il test avvia un sub-job asincrono e attende
              il completamento via cronologia AI_JOB.
            </p>
            <label className="field">
              <span>Parametri campione (exposeParams)</span>
              <textarea
                rows={8}
                className="mono"
                value={sampleParams}
                onChange={(e) => setSampleParams(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn primary"
              disabled={!!busy}
              onClick={() =>
                void runAction("test-model", async () => {
                  const cfg = createCurrentsApiConfig();
                  setModelTestSteps([]);
                  setModelTestResult(null);
                  setModelTestImageUrl(null);

                  const appendStep = (step: ModelTestLogStep) => {
                    setModelTestSteps((prev) => mergeModelTestStep(prev, step));
                  };

                  const result = await CurrentsAPI.testModelSkill(cfg, skillId, sampleParams, undefined, {
                    onStep: appendStep,
                  });
                  setModelTestResult(result);
                  if (result.steps?.length) {
                    setModelTestSteps(result.steps);
                  }

                  if (result.errorMessage) return;

                  if (result.jobId && result.s3Key) {
                    appendStep({
                      id: "presign_url",
                      label: "Generazione URL anteprima (presigned GET)",
                      status: "running",
                    });
                    const url = await resolveJobOutputImageUrl(result.jobId);
                    appendStep({
                      id: "presign_url",
                      label: url ? "URL anteprima pronto" : "URL anteprima non disponibile",
                      status: url ? "ok" : "warn",
                      detail: url ?? "admin-api /output-url",
                    });
                    if (url) {
                      setModelTestImageUrl(url);
                      appendStep({
                        id: "image_preview",
                        label: "Anteprima immagine caricata in UI",
                        status: "ok",
                      });
                    }
                  }
                })
              }
            >
              {busy === "test-model" ? "Generazione immagine…" : "Test generazione immagine"}
            </button>

            {(modelTestSteps.length > 0 || modelTestResult) && (
              <>
                {modelTestResult?.errorMessage && (
                  <p className="err" style={{ marginTop: "1rem" }}>
                    {modelTestResult.errorMessage}
                  </p>
                )}
                <ModelTestTracePanel
                  steps={modelTestSteps}
                  imageUrl={modelTestImageUrl}
                  mime={modelTestResult?.mime}
                  prompt={modelTestResult?.prompt}
                  durationMs={modelTestResult?.durationMs}
                  costCents={modelTestResult?.costCents}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
